import { useRef, useState, useLayoutEffect, useMemo } from 'react';
import {
  Paper,
  Box,
  Typography,
  Button,
  Avatar,
  Chip as MUIChip,
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import union_left from '../icons/union_left.svg';
import union_right from '../icons/union_right.svg';
import add_boards from '../icons/add_boards.svg';
import goal_2 from '../icons/goal_2.svg';
import dateFormat from '../utils/dateFormat';
import categoryBenefitsMap from '../utils/categoryBenefitsMap';
import Treatment from './Treatment';
import { featureFlags } from '../splitSetup';

const BoardCard = (props) => {
  const {
    board,
    hideMemberSince,
    hideHeadline,
    bigCompanyName,
    companyList,
    onClick,
    width,
    height,
    Chip = null,
    displayPillsFlag,
    goalImage,
    staticTile,
    cardId,
    buttonText,
    buttonId,
    customImage,
    headlineMarginBottom,
  } = props;
  const parentfileId = 'a5278712-a5e3-4bbb-89e1-4cfcc678e87f';
  const wonderDadsId = 'cf9c6fab-92b5-41a8-ba3c-0505fd778fd5';
  const [duplicates, setDuplicates] = useState(0);
  // const parentfileId = '9eccc760-d90f-45fb-96a3-b3b717c6f1b0'; // incative
  // const wonderDadsId = 'c526bb11-a138-42fa-b5ac-37abaa6cf0d2'; // cleaning

  const ParentfileChip = () => (
    <MUIChip
      label="1% Equity Pledge"
      size="small"
      sx={{
        position: 'absolute',
        fontSize: '12px',
        height: '20px',
        top: -10,
        right: { xxs: -12, xs: -10 },
        zIndex: 10,
        backgroundColor: '#f3f4f6',
        fontWeight: 500,
        padding: '13px 25px',
      }}
    />
  );
  const WonderDadsChip = () => (
    <MUIChip
      label="Limited Space Available"
      size="small"
      sx={{
        position: 'absolute',
        fontSize: '12px',
        height: '20px',
        top: -10,
        right: { xxs: -12, xs: -10 },
        zIndex: 10,
        backgroundColor: '#FDE2E0',
        fontWeight: 500,
        padding: '13px 25px',
      }}
    />
  );

  const history = useHistory();
  const dateObj = board.createdAt ? new Date(board.createdAt) : new Date();
  const year = dateFormat(dateObj, 'yyyy');
  const haveImage =
    (board.companyLogo && !board.companyLogo.includes('undefined')) ||
    board.image?.location;

  const allPills = useMemo(() => {
    const featured = board.benefits?.filter((b) => b.featured) || [];
    const counts = featured.reduce((acc, obj) => {
      acc[obj.category] = (acc[obj.category] || 0) + 1;
      return acc;
    }, {});
    const totalDuplicates = Object.values(counts).reduce((sum, count) => {
      return sum + (count > 1 ? count - 1 : 0);
    }, 0);
    setDuplicates(totalDuplicates);

    const pills = [];
    const seen = new Set();

    [...featured]
      .sort((a, b) => a.order - b.order)
      .forEach(({ category }) => {
        if (!seen.has(category) && categoryBenefitsMap[category]) {
          seen.add(category);
          const { icon, label } = categoryBenefitsMap[category];
          pills.push({ icon, label });
        }
      });

    if (featured.length < 4) {
      pills.push({ icon: '📝', label: 'Add to Resume' });
    }
    return pills;
  }, [board.benefits]);

  const pillsContainerRef = useRef(null);
  const [renderedPills, setRenderedPills] = useState(allPills);

  useLayoutEffect(() => {
    const container = pillsContainerRef.current;
    if (!container) {
      setRenderedPills(allPills);
      return;
    }
    const chips = Array.from(container.children);
    if (!chips.length) {
      setRenderedPills(allPills);
      return;
    }

    const GAP = 8; // spacing(1) en MUI
    const firstH = chips[0].getBoundingClientRect().height;
    const rowHeight = firstH + GAP;
    const maxHeight = rowHeight * 2;

    let visibleCount = 0;
    for (let chip of chips) {
      const bottom =
        chip.offsetTop - container.offsetTop + chip.offsetHeight - 24;
      if (bottom <= maxHeight) visibleCount++;
      else break;
    }

    let newList;
    if (allPills.length > visibleCount) {
      const overflowCount = allPills.length - (visibleCount - 1);
      newList = [
        ...allPills.slice(0, visibleCount - 1),
        { icon: '+', label: `${overflowCount + duplicates} more` },
      ];
    } else {
      newList = allPills;
      if (duplicates > 0) {
        newList.push({ icon: '+', label: `${duplicates} more` });
      }
    }

    const same =
      newList.length === renderedPills.length &&
      newList.every((p, i) => p.label === renderedPills[i]?.label);
    if (!same) {
      setRenderedPills(newList);
    }
  }, [allPills, width]); // sólo se re-ejecuta si cambia la referencia de allPills o el ancho

  const getHeight = () => {
    if (height) return height;
    if (!board.createdAt && !staticTile) return 'auto';
    return displayPillsFlag || staticTile ? '330px' : '295px';
  };

  return (
    <Paper
      id={cardId}
      elevation={1}
      className={
        board.createdAt
          ? `my-board-${board.boardType === 'P2P' ? 'p2p' : 'card'}`
          : 'view-boardrooms'
      }
      sx={{
        height: getHeight(),
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: companyList ? '0px 16px 16px' : '16px',
        borderRadius: '12px',
        textAlign: 'center',
        boxShadow: '0px 10px 12px 0px #0010300D',
        width: width || '100%',
      }}
    >
      <div
        style={{
          width: 'calc(100% + 32px)',
          left: '-16px',
          height: '56px',
          background: 'linear-gradient(#2C71F633, #25A4FF33)',
          borderRadius: '8px 8px 0px 0px',
          position: 'relative',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
        }}
      >
        {Chip && ![parentfileId, wonderDadsId].includes(board?.id) && <Chip />}

        {Chip && board?.id === parentfileId && <ParentfileChip />}
        {Chip && board?.id === wonderDadsId && <WonderDadsChip />}

        <img
          src={union_left}
          alt="left"
          style={{ position: 'absolute', zIndex: 1, left: 0, marginTop: 25 }}
        />
        <img
          src={union_right}
          alt="right"
          style={{ position: 'absolute', zIndex: 1, right: 0 }}
        />
        <Box
          sx={{
            zIndex: 2,
            position: 'absolute',
            marginTop: '16px',
            height: 72,
            width: 72,
            backgroundColor: 'white',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Avatar
            src={
              customImage
                ? customImage
                : haveImage
                ? `https://${board.companyLogo || board.image?.location}`
                : goalImage
                ? goal_2
                : add_boards
            }
            sx={{
              height: customImage || haveImage ? '100%' : 60,
              width: customImage || haveImage ? '100%' : 60,
              borderRadius: '8px',
            }}
          />
        </Box>
      </div>
      <Box sx={{ marginTop: '38px' }}>
        <Typography
          sx={{
            fontSize: bigCompanyName ? '24px' : '16px',
            fontWeight: 600,
            mb: !board.createdAt ? '10px' : 0,
            textOverflow: bigCompanyName ? 'none' : 'ellipsis',
            overflow: bigCompanyName ? 'visible' : 'hidden',
            whiteSpace: bigCompanyName ? 'normal' : 'nowrap',
          }}
        >
          {board.companyName}
        </Typography>
        {!hideMemberSince && board.createdAt && (
          <Typography
            sx={{
              fontSize: '12px',
              color: '#4D586F',
              fontWeight: 400,
              mb: '5px',
            }}
          >
            Board Member Since: {year}
          </Typography>
        )}
        {board.headline && !hideHeadline && (
          <Typography
            sx={{
              fontSize: '12px',
              color: '#4D586F',
              fontWeight: 400,
              lineHeight: '18px',
              mb: headlineMarginBottom || '6px',
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: staticTile ? 4 : { xxs: 3, xs: 2 },
              WebkitBoxOrient: 'vertical',
              whiteSpace: 'normal',
            }}
          >
            {board.headline}
          </Typography>
        )}
      </Box>

      {displayPillsFlag && renderedPills.length > 0 && (
        <Box
          ref={pillsContainerRef}
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 1,
            maxWidth: 'calc(100% + 20px)',
            boxSizing: 'border-box',
            mb: 1,
            mx: '-10px',
            maxHeight: '90px',
            overflow: 'hidden',
          }}
        >
          {renderedPills.map((pill) => (
            <MUIChip
              key={pill.label}
              size="small"
              icon={<span style={{ fontSize: 12 }}>{pill.icon}</span>}
              label={pill.label}
              sx={{
                fontSize: '12px',
                fontWeight: 500,
                px: 0.8,
                py: 0.4,
                borderRadius: '1em',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                backgroundColor: '#f3f4f6',
              }}
              aria-label={pill.label}
            />
          ))}
        </Box>
      )}

      <Treatment
        splitNames={featureFlags.update_board_directory}
        evaluatedComponent={
          <Button
            id={buttonId}
            variant="outlined"
            className={companyList ? 'view-company-button' : ''}
            onClick={() => {
              if (onClick) onClick();
              else if (board.createdAt)
                history.push(`/${board.slug}/boardroom`);
              else history.push('/board-directory');
            }}
            disableRipple
            sx={{
              textTransform: 'none',
              fontSize: '14px',
              p: '6px 12px',
              borderRadius: 2,
              backgroundColor: 'hsla(219,92%,57%,1)',
              '&:hover': {
                backgroundColor: 'hsla(219,92%,57%,1)',
              },
              color: 'white',
            }}
          >
            {buttonText ? buttonText : board.createdAt ? 'View' : 'View Boards'}
          </Button>
        }
        conditionFailedComponent={
          <Button
            id={buttonId}
            variant="outlined"
            className={companyList ? 'view-company-button' : ''}
            onClick={() => {
              if (onClick) onClick();
              else if (board.createdAt)
                history.push(`/${board.slug}/boardroom`);
              else history.push('/board-directory');
            }}
            sx={{
              textTransform: 'none',
              fontSize: '14px',
              p: '6px 12px',
              borderRadius: 2,
              borderColor: 'hsla(219,92%,57%,1)',
              color: 'hsla(219,92%,57%,1)',
              borderWidth: '1px',
              backgroundColor: 'white',
            }}
          >
            {buttonText ? buttonText : board.createdAt ? 'View' : 'View Boards'}
          </Button>
        }
      />
    </Paper>
  );
};

export default BoardCard;
